/*
 * Migration Lot 3 (2026-04-30) : `ModalUI` est désormais bâti directement
 * sur les primitives Radix Dialog (et non sur le `DialogContent` de
 * shadcn) pour préserver l'API et le comportement visuel hérité de MUI :
 *
 *   - PAS de bouton de fermeture X automatique : les consommateurs en
 *     dessinent un eux-mêmes (`<span className="lc-modal-icon-cross">`).
 *     Utiliser shadcn `DialogContent` ajouterait un X en plus → doublon.
 *   - PAS de contrainte `max-w-lg` : l'ancien sx MUI utilisait
 *     `width: 50%`, on reproduit avec `w-1/2` sans cap.
 *   - PAS d'animation slide : MUI ne sliddait pas, on évite la surprise.
 *   - La prop `styles` (héritée de l'ancien wrapper) est un objet CSS
 *     standard appliqué via `style={...}` — l'audit du code consommateur
 *     a confirmé qu'aucun callsite ne passe de tokens MUI (`bgcolor:
 *     "background.paper"` etc.), uniquement des valeurs CSS plates
 *     (`top: 0`, `width: "25%"`, `transform: "unset"`...).
 *
 * 9 consommateurs : Assureur, AssureurDetails/Edit, Examen, Patient,
 * PersonnelMedecin, Profile, Users (index + ShowUser), Payment/
 * FinancialTransactionModal. Migration sans toucher leur code.
 */
import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cn } from "@/lib/utils";

// Note défensive : `className = ""` (et non `className`) pour que TS
// infère la prop comme optionnelle quand des consommateurs `.tsx`
// référencent ce module `.js` sans la passer.
export default function ModalUI({ children, isOpen, onClose, styles, className = "" }) {
  // Esc / clic overlay → onOpenChange(false). On normalise vers l'API
  // MUI historique (`onClose` invoqué uniquement à la fermeture).
  const handleOpenChange = (next) => {
    if (!next) onClose?.();
  };

  return (
    <DialogPrimitive.Root open={isOpen} onOpenChange={handleOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            // z-[110] passe au-dessus de la sidebar (z-100) et du nav (z-90)
            // de DashboardWrapper. Sans ce bump, l'overlay scrim n'occulte
            // pas les zones de chrome — régression UI signalée 2026-05-01.
            "fixed inset-0 z-[110] bg-black/60 backdrop-blur-sm",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
          )}
        />
        <DialogPrimitive.Content
          // Les consommateurs n'ont pas (ou rarement) de description,
          // on évite la console.warn Radix sur aria-describedby manquant.
          aria-describedby={undefined}
          className={cn(
            // Centrage MUI : top/left 50% + translate, repris à l'identique.
            // z-[120] = strictement au-dessus de l'overlay z-[110].
            "fixed left-1/2 top-1/2 z-[120] -translate-x-1/2 -translate-y-1/2",
            // Largeur 50% (pas de max-w-lg shadcn). max-h pour éviter overflow.
            "w-1/2 max-h-[90vh] overflow-auto",
            // Carte blanche, padding 32px (≈ p:4 MUI), arrondi, ombre.
            "rounded-lg border bg-background p-8 shadow-lg",
            // Fade in/out (pas de slide pour rester proche du visuel MUI).
            "duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "focus:outline-none",
            className,
          )}
          // L'ancien wrapper acceptait un objet `styles` (sx MUI sous nom
          // trompeur) — on le pousse en inline, ce qui bat les classes
          // Tailwind ci-dessus (cas typique : `width: "25%"` pour les
          // modales en drawer côté droit).
          style={styles}
        >
          {/*
            DialogTitle requis par Radix pour l'a11y. L'ancien wrapper MUI
            ne rendait pas de titre — on cache visuellement avec sr-only.
          */}
          <DialogPrimitive.Title className="sr-only">Modal</DialogPrimitive.Title>
          {children}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
